import React, { Suspense } from "react";
import { lazyWithRetry as lazy, isChunkError } from "@/lib/lazyWithRetry";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, useLocation, Navigate, useParams } from "react-router-dom";
import { ScrollToTop } from "@/components/ScrollToTop";
import SiteTracker from "@/components/SiteTracker";
import SiteAvailabilityRefresh from '@/components/SiteAvailabilityRefresh';
import { HelmetProvider } from "react-helmet-async";
import { CartProvider } from "@/contexts/CartContext";
import { SizePreferenceProvider } from "@/contexts/SizePreferenceContext";
import { FavoritesProvider } from "@/contexts/FavoritesContext";
import { AdminThemeProvider } from "@/contexts/AdminThemeContext";
import { SiteThemeProvider } from "@/contexts/SiteThemeContext";
import ConstructionGate from "@/components/ConstructionGate";
import { reportBoundaryError } from "@/lib/diagnostics/clientTelemetry";

import DeferredCartDrawer from "@/components/DeferredCartDrawer";
const BottomBar = lazy(() => import("@/components/BottomBar"));
import { hasSiteBottomNav } from "@/lib/siteBottomNav";

const CookieConsent = lazy(() => import("@/components/CookieConsent"));
import { toast } from "sonner";

// Homepage é lazy: subdomínios (catálogo, vagas, ouvidoria, painel) não
// precisam baixar o shell da home junto do bundle inicial.
const Index = lazy(() => import("./pages/Index"));
const NotFound = lazy(() => import("./pages/NotFound"));

// Lazy-loaded public pages (critical but not needed on initial homepage render)
const Vitrine = lazy(() => import("./pages/Vitrine"));
const ProdutoPage = lazy(() => import("./pages/ProdutoPage"));
const Busca = lazy(() => import("./pages/Busca"));
const Auth = lazy(() => import("./pages/Auth"));

// Lazy-loaded public pages (secondary)

const MeusPedidos = lazy(() => import("./pages/MeusPedidos"));
const PedidoConfirmado = lazy(() => import("./pages/PedidoConfirmado"));
const SolicitarTroca = lazy(() => import("./pages/SolicitarTroca"));
const Rastreio = lazy(() => import("./pages/Rastreio"));
const AcompanharPedido = lazy(() => import("./pages/AcompanharPedido"));
const RastreioResultado = lazy(() => import("./pages/RastreioResultado"));
const AdminSolicitarDevolucao = lazy(() => import("./pages/admin/AdminSolicitarDevolucao"));
const AdminReviews = lazy(() => import("./pages/admin/AdminReviews"));
// Fixture LOCAL de QA visual (somente em desenvolvimento; sem autenticação e
// sem tocar dados reais) — a rota só existe sob import.meta.env.DEV.
const AdminReviewsFixture = lazy(() => import("./pages/admin/AdminReviewsFixture"));
const AdminRemoteAuth = lazy(() => import("./pages/admin/AdminRemoteAuth"));
const Categorias = lazy(() => import("./pages/Categorias"));
const Promocao24h = lazy(() => import("./pages/Promocao24h"));
const Feedback = lazy(() => import("./pages/Feedback"));
const ReviewInvite = lazy(() => import("./pages/ReviewInvite"));
const Legal = lazy(() => import("./pages/Legal"));
const Unsubscribe = lazy(() => import("./pages/Unsubscribe"));
const NewsletterConfirm = lazy(() => import("./pages/NewsletterConfirm"));
const AdminCompreLook = lazy(() => import("./pages/admin/AdminCompreLook"));
const Videos = lazy(() => import("./pages/Videos"));
const Achadinhos = lazy(() => import("./pages/Achadinhos"));
const Looks = lazy(() => import("./pages/Looks"));
const Checkout = lazy(() => import("./pages/Checkout"));
const RetomarCarrinho = lazy(() => import("./pages/RetomarCarrinho"));
const ResetPassword = lazy(() => import("./pages/ResetPassword"));
const PedidoPix = lazy(() => import("./pages/PedidoPix"));
const PagarComplemento = lazy(() => import("./pages/PagarComplemento"));
const PagarLink = lazy(() => import("./pages/PagarLink"));
const Grupos = lazy(() => import("./pages/Grupos"));
const Catalogo = lazy(() => import("./pages/Catalogo"));
const LIVE_COLLECTION = { slug: "live", title: "Live" } as const;
const Vagas = lazy(() => import("./pages/Vagas"));
const Ouvidoria = lazy(() => import("./pages/Ouvidoria"));
const Cashback = lazy(() => import("./pages/Cashback"));
const AdminOuvidoria = lazy(() => import("./pages/admin/AdminOuvidoria"));

// Lazy-loaded admin pages (huge bundle savings)
const AdminLogin = lazy(() => import("./pages/admin/AdminLogin"));
const AdminDashboard = lazy(() => import("./pages/admin/AdminDashboard"));
const AdminProducts = lazy(() => import("./pages/admin/AdminProducts"));
const AdminOrders = lazy(() => import("./pages/admin/AdminOrders"));
const AdminCustomers = lazy(() => import("./pages/admin/AdminCustomers"));
const AdminPDV = lazy(() => import("./pages/admin/AdminPDV"));
const AdminManualOrder = lazy(() => import("./pages/admin/AdminManualOrder"));
const AdminCalculadoraFrete = lazy(() => import("./pages/admin/AdminCalculadoraFrete"));
const AdminFulfillment = lazy(() => import("./pages/admin/AdminFulfillment"));
const AdminExchanges = lazy(() => import("./pages/admin/AdminExchanges"));
const AdminSettings = lazy(() => import("./pages/admin/AdminSettings"));
const AdminDiagnostico = lazy(() => import("./pages/admin/AdminDiagnostico"));
const AdminTikTokShop = lazy(() => import("./pages/admin/AdminTikTokShop"));
const AdminRecovery = lazy(() => import("./pages/admin/AdminRecovery"));
const AdminAtribuicao = lazy(() => import("./pages/admin/AdminAtribuicao"));
const AdminConversao = lazy(() => import("./pages/admin/AdminConversao"));
const AdminDesempenhoProdutos = lazy(() => import("./pages/admin/AdminDesempenhoProdutos"));
const AdminMarketing = lazy(() => import("./pages/admin/AdminMarketing"));
const AdminCampanhas = lazy(() => import("./pages/admin/AdminCampanhas"));
const AdminBeneficios = lazy(() => import("./pages/admin/AdminBeneficios"));
const AdminTarefas = lazy(() => import("./pages/admin/AdminTarefas"));
const AdminTaskMonitor = lazy(() => import("./pages/admin/AdminTaskMonitor"));
const AdminNotifications = lazy(() => import("./pages/admin/AdminNotifications"));
const AdminNotificationsSettings = lazy(() => import("./pages/admin/AdminNotificationsSettings"));
const AdminNotificationsPanel = lazy(() => import("./pages/admin/AdminNotificationsPanel"));
const AdminCoupons = lazy(() => import("./pages/admin/AdminCoupons"));
const AdminQuantityRules = lazy(() => import("./pages/admin/AdminQuantityRules"));
const AdminCampaignDiscounts = lazy(() => import("./pages/admin/AdminCampaignDiscounts"));
// Moldura/guardas do painel: só baixam em rotas /admin (fora do código inicial do site).
const AdminRailShell = lazy(() => import("./components/admin/AdminRailShell"));
const PageEditGuard = lazy(() => import("./components/admin/PageEditGuard"));
const RequireEditPermission = lazy(() => import("./components/admin/RequireEditPermission"));
const AdminLayoutSite = lazy(() => import("./pages/admin/AdminLayoutSite"));
const AdminNovidades = lazy(() => import("./pages/admin/AdminNovidades"));
const AdminLancamentos = lazy(() => import("./pages/admin/AdminLancamentos"));
const AdminCentralLancamentos = lazy(() => import("./pages/admin/AdminCentralLancamentos"));
const LaunchCentralRedirect = lazy(() => import("./pages/admin/LaunchCentralRedirect"));
const AdminConferirPix = lazy(() => import("./pages/admin/AdminConferirPix"));
const AdminGastosPendentes = lazy(() => import("./pages/admin/AdminGastosPendentes"));
const AdminProductAnalysis = lazy(() => import("./pages/admin/AdminProductAnalysis"));
const AdminSupplies = lazy(() => import("./pages/admin/AdminSupplies"));
const AdminSmartCollections = lazy(() => import("./pages/admin/AdminSmartCollections"));
const AdminVitrineOrdem = lazy(() => import("./pages/admin/AdminVitrineOrdem"));
const AdminBannerHome = lazy(() => import("./pages/admin/AdminBannerHome"));
const AdminBannerVitrine = lazy(() => import("./pages/admin/AdminBannerVitrine"));
const AdminCatalogoOutlet = lazy(() => import("./pages/admin/AdminCatalogoOutlet"));
const OutletVip = lazy(() => import("./pages/OutletVip"));

const AdminFiscal = lazy(() => import("./pages/admin/AdminFiscal"));
const AdminEtiquetas = lazy(() => import("./pages/admin/AdminEtiquetas"));
const AdminEstoque = lazy(() => import("./pages/admin/AdminEstoque"));
const AdminTransferencias = lazy(() => import("./pages/admin/AdminTransferencias"));
const AdminReservas = lazy(() => import("./pages/admin/AdminReservas"));
const AdminEstoqueRegistrar = lazy(() => import("./pages/admin/AdminEstoqueRegistrar"));
const AdminServicos = lazy(() => import("./pages/admin/AdminServicos"));
const AdminServicoNovo = lazy(() => import("./pages/admin/AdminServicoNovo"));
const AdminSaidas = lazy(() => import("./pages/admin/AdminSaidas"));
const AdminRevisao = lazy(() => import("./pages/admin/AdminRevisao"));
const AdminSaidaNova = lazy(() => import("./pages/admin/AdminSaidaNova"));
const AdminEntradas = lazy(() => import("./pages/admin/AdminEntradas"));
const AdminEntradaNova = lazy(() => import("./pages/admin/AdminEntradaNova"));
const AdminInventoryList = lazy(() => import("./pages/admin/AdminInventoryList"));
const AdminInventoryCount = lazy(() => import("./pages/admin/AdminInventoryCount"));
const AdminRackCuration = lazy(() => import("./pages/admin/AdminRackCuration"));
const AdminEmailTemplateEditor = lazy(() => import("./pages/admin/AdminEmailTemplateEditor"));
const PDVClienteDisplay = lazy(() => import("./pages/admin/PDVClienteDisplay"));
const PDVAutoatendimento = lazy(() => import("./pages/admin/PDVAutoatendimento"));
const AdminEmployees = lazy(() => import("./pages/admin/AdminEmployees"));
const AdminRodizio = lazy(() => import("./pages/admin/AdminRodizio"));
const AdminRodizioTV = lazy(() => import("./pages/admin/AdminRodizioTV"));
const AdminSeparacaoTV = lazy(() => import("./pages/admin/AdminSeparacaoTV"));
const AdminWorkSchedule = lazy(() => import("./pages/admin/AdminWorkSchedule"));
const AdminTimeClock = lazy(() => import("./pages/admin/AdminTimeClock"));
const AdminTimeRecords = lazy(() => import("./pages/admin/AdminTimeRecords"));
const AdminStoreCalendar = lazy(() => import("./pages/admin/AdminStoreCalendar"));
const AdminSalesGoals = lazy(() => import("./pages/admin/AdminSalesGoals"));
const AdminMinhasMetas = lazy(() => import("./pages/admin/AdminMinhasMetas"));
const AdminMeuCalendario = lazy(() => import("./pages/admin/AdminMeuCalendario"));

const AdminCaixas = lazy(() => import("./pages/admin/AdminCaixas"));
const AdminRFID = lazy(() => import("./pages/admin/AdminRFID"));
const AdminOutrosProdutos = lazy(() => import("./pages/admin/AdminOutrosProdutos"));
const AdminFeedback = lazy(() => import("./pages/admin/AdminFeedback"));
const AdminSystemStatus = lazy(() => import("./pages/admin/AdminSystemStatus"));
const AdminShippingLabelPrint = lazy(() => import("./pages/admin/AdminShippingLabelPrint"));
const AdminExpenses = lazy(() => import("./pages/admin/AdminExpenses"));
const AdminDRE = lazy(() => import("./pages/admin/AdminDRE"));
const AdminCompras = lazy(() => import("./pages/admin/AdminCompras"));
const AdminFamilyExpenses = lazy(() => import("./pages/admin/AdminFamilyExpenses"));
const AdminVagas = lazy(() => import("./pages/admin/AdminVagas"));
const AdminMovimentacoes = lazy(() => import("./pages/admin/AdminMovimentacoes"));
const AdminServicosExternos = lazy(() => import("./pages/admin/AdminServicosExternos"));
const AdminColaboradoresHub = lazy(() => import("./pages/admin/AdminColaboradoresHub"));
const AdminStudioPhotos = lazy(() => import("./pages/admin/AdminStudioPhotos"));
const AdminStudioUpload = lazy(() => import("./pages/admin/AdminStudioUpload"));
const AdminStudioPhotoSession = lazy(() => import("./pages/admin/AdminStudioPhotoSession"));
const AdminTelaVitrine = lazy(() => import("./pages/admin/AdminTelaVitrine"));
const TelaPromo = lazy(() => import("./pages/TelaPromo"));

// Lazy loading fallback - shimmer bar + bouncing dots (discreet, non-branded)
const PageLoaderLazy = lazy(() => import("./components/admin/PageLoader"));
const PageLoader = () => <Suspense fallback={<div className="min-h-screen bg-background" />}><PageLoaderLazy /></Suspense>;
import {
  CORE_SITE_ROUTES,
  initSiteLinkPrefetch,
  prefetchSiteRoutesIdle,
  isDirectProductEntry,
} from "@/lib/sitePrefetch";

/**
 * No site (storefront) priorizamos a sensação de instantaneidade: como os chunks
 * das páginas já são prefetchados no hover/toque dos links, não mostramos loader
 * — apenas nada por alguns milissegundos, evitando o "flash" de carregando.
 * No painel admin mantemos o PageLoader.
 */
const LazyFallback = () => {
  const { pathname } = useLocation();
  if (pathname.startsWith("/admin")) return <PageLoader />;
  // Site: esqueleto discreto da grade (sem marca/texto), evita tela em branco em rede lenta.
  return (
    <div className="min-h-screen bg-background pt-14 md:pt-20" aria-busy="true" aria-label="Carregando">
      <div className="h-36 animate-pulse bg-muted md:h-64" />
      <div className="mx-auto grid max-w-7xl grid-cols-2 gap-3 px-4 py-6 md:grid-cols-4 md:px-8">
        {Array.from({ length: 4 }).map((_, i) => (
          <div key={i} className="aspect-[3/4] animate-pulse rounded-sm bg-muted" />
        ))}
      </div>
    </div>
  );
};

/** Aquece os chunks das páginas mais usadas do site em tempo ocioso. */
const SiteRoutePrefetcher = () => {
  const { pathname } = useLocation();
  const isAdmin = pathname.startsWith("/admin");
  // Escuta de intenção (hover/toque/foco) registrada uma vez, com limpeza.
  React.useEffect(() => {
    if (isAdmin) return;
    return initSiteLinkPrefetch();
  }, [isAdmin]);
  // Aquecimento ocioso só das rotas principais, uma vez por sessão e nunca em economia de dados.
  React.useEffect(() => {
    if (isAdmin || isDirectProductEntry()) return;
    prefetchSiteRoutesIdle(CORE_SITE_ROUTES);
  }, [isAdmin]);
  return null;
};


// Detect catalogo subdomain at module level (host doesn't change during SPA navigation)
const isCatalogoHost =
  typeof window !== "undefined" && window.location.hostname === "catalogo.miahma.com.br";
const isPainelHost =
  typeof window !== "undefined" && window.location.hostname === "painel.miahma.com.br";
const isLinksGruposHost =
  typeof window !== "undefined" &&
  window.location.hostname === "link-grupos.miahma.com.br";
const isVagasHost =
  typeof window !== "undefined" &&
  window.location.hostname === "vagas.miahma.com.br";
const isOuvidoriaHost =
  typeof window !== "undefined" &&
  window.location.hostname === "ouvidoria.miahma.com.br";
const isCashbackHost =
  typeof window !== "undefined" &&
  window.location.hostname === "cashback.miahma.com.br";
const isTelaPromoHost =
  typeof window !== "undefined" &&
  window.location.hostname === "telapromo.miahma.com.br";

import { resolveCategorySlug } from "@/lib/siteTaxonomy";

const LegacyProductRedirect = () => {
  const { id } = useParams();
  return <Navigate to={`/produto/${id}`} replace />;
};

/** /colecao/:slug antigo — vira /categoria/:slug quando o slug é uma categoria. */
const LegacyCollectionRedirect = () => {
  const { slug } = useParams();
  const isCategory = !!resolveCategorySlug(slug);
  if (isCategory) return <Navigate to={`/categoria/${slug}`} replace />;
  return <Vitrine preset="colecao" />;
};


// Component to conditionally render BottomBar and Desktop Floating Buttons
const ConditionalFloatingElements = () => {
  const location = useLocation();
  // Payment layouts, product page (own buy bar), admin/catalog hosts: no storefront nav.
  if (!hasSiteBottomNav(location.pathname)) return null;

  return (
    <>
      <BottomBar />
    </>
  );
};

// Cookie consent only for public routes
const ConditionalCookieConsent = () => {
  const location = useLocation();
  const isAdminRoute = isPainelHost || location.pathname.startsWith("/admin");
  const isCatalogoRoute = isCatalogoHost || location.pathname.startsWith("/catalogo");

  if (isAdminRoute || isCatalogoRoute || isLinksGruposHost || isVagasHost || isOuvidoriaHost || isCashbackHost || isTelaPromoHost || location.pathname.startsWith("/tela-promo")) return null;
  return <CookieConsent />;
};

// Dynamic page title based on route
const DynamicTitle = () => {
  const location = useLocation();

  React.useEffect(() => {
    const path = location.pathname;
    const host = typeof window !== "undefined" ? window.location.hostname : "";
    const isCatalogo = host === "catalogo.miahma.com.br" || path.startsWith("/catalogo");
    const isAdmin = host === "painel.miahma.com.br" || path.startsWith("/admin");

    if (isCatalogo) {
      document.title = "Catálogo Miahma";
    } else if (isAdmin) {
      document.title = "Painel Miahma";
    } else {
      document.title = "Miahma | Moda Feminina";
    }
  }, [location.pathname]);

  return null;
};

// Wrapper that applies ConstructionGate only to public routes
const PublicRouteWrapper = ({ children }: { children: React.ReactNode }) => {
  const location = useLocation();
  const isAdminRoute = isPainelHost || location.pathname.startsWith("/admin");
  const isCatalogoRoute = isCatalogoHost || location.pathname.startsWith("/catalogo");

  if (isAdminRoute || isCatalogoRoute || isLinksGruposHost || isVagasHost || isOuvidoriaHost || isCashbackHost || isTelaPromoHost || location.pathname.startsWith("/tela-promo")) return <>{children}</>;
  return <ConstructionGate>{children}</ConstructionGate>;
};

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 1000 * 60 * 5,
      gcTime: 1000 * 60 * 10,
      refetchOnWindowFocus: false,
      retry: 1,
    },
  },
});

class ErrorBoundary extends React.Component<
  { children: React.ReactNode },
  { hasError: boolean; error: Error | null }
> {
  constructor(props: { children: React.ReactNode }) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error: Error) {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    console.error("ErrorBoundary caught:", error, errorInfo);
    reportBoundaryError(error);
    // Tela de erro nunca deve ser indexada (ex.: buscador renderizando durante uma atualização).
    try {
      let m = document.querySelector('meta[name="robots"][data-error]') as HTMLMetaElement | null;
      if (!m) { m = document.createElement("meta"); m.name = "robots"; m.setAttribute("data-error", ""); document.head.appendChild(m); }
      m.content = "noindex";
    } catch { /* ignore */ }
    if (isChunkError(error)) {
      try {
        const k = "miahma-chunk-reload";
        const last = Number(sessionStorage.getItem(k) || 0);
        if (Date.now() - last > 60_000) { sessionStorage.setItem(k, String(Date.now())); window.location.reload(); }
      } catch { /* ignore */ }
    }
  }

  render() {
    if (this.state.hasError) {
      return (
        <div className="min-h-screen flex items-center justify-center bg-background p-6">
          <div className="text-center space-y-4 max-w-md">
            <h2 className="text-xl font-bold text-foreground">Algo deu errado</h2>
            <p className="text-muted-foreground text-sm">
              {isChunkError(this.state.error)
                ? "Atualizamos o site. Recarregue a página para continuar."
                : "Não foi possível carregar esta página. Tente recarregar."}
            </p>
            <button
              onClick={() => {
                this.setState({ hasError: false, error: null });
                window.location.href = window.location.pathname;
              }}
              className="px-6 py-2 bg-primary text-primary-foreground rounded-md hover:opacity-90"
            >
              Recarregar
            </button>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

if (typeof window !== "undefined") {
  const host = window.location.hostname;
  const path = window.location.pathname;
  const isClientDomain =
    host === "site.miahma.com.br" ||
    host === "miahma.com.br" ||
    host === "www.miahma.com.br";

  // miahma.com.br e www.miahma.com.br servem o site normalmente (respeitando ConstructionGate)
  const isPainelDomain = host === "painel.miahma.com.br";
  const isCatalogoDomain = host === "catalogo.miahma.com.br";

  // Client domains: block /admin and /catalogo routes → redirect to home
  if (isClientDomain && (path.startsWith("/admin") || path.startsWith("/catalogo"))) {
    window.location.replace("/" + window.location.search + window.location.hash);
  }

  // Endereço técnico de publicação: rotas administrativas vão para o painel
  // oficial, SEM levar query string ou hash (nada de token, sessão ou
  // parâmetro sensível na URL).
  if (host === "sitemiahma.lovable.app" && path.startsWith("/admin")) {
    window.location.replace("https://painel.miahma.com.br" + path);
  }

  // Painel domain: reescreve o path para /admin ANTES do React montar,
  // evitando o flash do site público enquanto o redirect acontece.
  if (isPainelDomain && !path.startsWith("/admin")) {
    window.history.replaceState(
      null,
      "",
      "/admin" + window.location.search + window.location.hash,
    );
  }

  // Catalogo domain: serve catalogo at root. Redirect /catalogo → / to keep URL clean.
  if (isCatalogoDomain && path.startsWith("/catalogo")) {
    window.location.replace("/" + window.location.search + window.location.hash);
  }

  window.addEventListener("unhandledrejection", (event) => {
    console.error("Unhandled rejection:", event.reason);
    toast.error("Ocorreu um erro inesperado. Tente novamente.");
    event.preventDefault();
  });
}

// Erro de uma página fica só nela: a chave por caminho limpa o erro ao navegar,
// e o resto do site/painel (topo, carrinho, demais rotas) continua de pé.
const RouteErrorBoundary = ({ children }: { children: React.ReactNode }) => {
  const { pathname } = useLocation();
  return <ErrorBoundary key={pathname}>{children}</ErrorBoundary>;
};

const App = () => (
  <ErrorBoundary>
    <HelmetProvider>
      <QueryClientProvider client={queryClient}>
        <SiteThemeProvider>
          <SizePreferenceProvider>
              <FavoritesProvider>
                <CartProvider>
                  <TooltipProvider>
                    <Toaster />
                    <Sonner />
                    <BrowserRouter>
                      <ScrollToTop />
                      <SiteTracker />
                      <SiteAvailabilityRefresh />
                      <SiteRoutePrefetcher />
                      <DynamicTitle />

                      <AdminThemeProvider>
                          <PublicRouteWrapper>
                            <DeferredCartDrawer />
                            <ConditionalFloatingElements />
                            <ConditionalCookieConsent />
                            <RouteErrorBoundary>
                            <Suspense fallback={<LazyFallback />}>
                              <Routes>
                                <Route path="/cashback" element={<Cashback />} />
                                <Route path="/" element={isCashbackHost ? <Cashback /> : isTelaPromoHost ? <TelaPromo /> : isOuvidoriaHost ? <Ouvidoria /> : isVagasHost ? <Vagas /> : isLinksGruposHost ? <Grupos /> : isCatalogoHost ? <Catalogo /> : <Index />} />
                                <Route path="/vagas" element={<Vagas />} />
                                <Route path="/colecao" element={<Vitrine preset="colecao" />} />
                                <Route path="/lancamentos" element={<Vitrine preset="lancamentos" />} />
                                <Route path="/promocoes" element={<Vitrine preset="sale" />} />
                                <Route path="/sale" element={<Navigate to="/promocoes" replace />} />
                                <Route path="/colecao/:slug" element={<LegacyCollectionRedirect />} />
                                <Route path="/produto/:id" element={<ProdutoPage />} />
                                <Route path="/p/:id" element={<LegacyProductRedirect />} />
                                <Route path="/entrar" element={<Auth />} />
                                
                                <Route path="/meus-pedidos" element={<MeusPedidos />} />
                                <Route path="/favoritos" element={<Navigate to="/colecao" replace />} />
                                <Route path="/pedido/:orderId" element={<PedidoConfirmado />} />
                                <Route path="/pedido/:orderId/troca" element={<SolicitarTroca />} />
                                <Route path="/pedido/:orderNumber/pix" element={<PedidoPix />} />
                                <Route path="/rastreio" element={<Rastreio />} />
                                <Route path="/acompanhar" element={<AcompanharPedido />} />
                                <Route path="/rastreio/:codigo" element={<RastreioResultado />} />
                                <Route path="/admin/trocas-devolucoes" element={<AdminSolicitarDevolucao />} />
                                <Route path="/admin/solicitar-devolucao" element={<Navigate to="/admin/trocas-devolucoes" replace />} />
                                <Route path="/admin/autorizacao/:token" element={<AdminRemoteAuth />} />
                                <Route path="/troca-facil" element={<Navigate to="/admin/trocas-devolucoes" replace />} />

                                <Route path="/categorias" element={<Categorias />} />
                                <Route path="/videos" element={<Videos />} />
                                <Route path="/achadinhos" element={<Achadinhos />} />
                                <Route path="/looks" element={<Looks />} />
                                <Route path="/compre-o-look" element={<Navigate to="/looks" replace />} />


                                <Route path="/categoria" element={<Vitrine preset="categoria" />} />
                                <Route path="/categoria/:slug" element={<Vitrine preset="categoria" />} />
                                <Route path="/pesquisar" element={<Busca />} />
                                <Route path="/busca" element={<Navigate to="/pesquisar" replace />} />
                                <Route path="/feedback" element={<Feedback />} />
                                <Route path="/avaliar/:token" element={<ReviewInvite />} />
                                {import.meta.env.DEV ? (
                                  <Route path="/fixture/avaliacoes" element={<AdminReviewsFixture />} />
                                ) : null}
                                <Route path="/termos-de-uso" element={<Legal kind="termos" />} />
                                <Route path="/politica-privacidade" element={<Legal kind="privacidade" />} />
                                <Route path="/politica-de-privacidade" element={<Navigate to="/politica-privacidade" replace />} />
                                <Route path="/politica-cookies" element={<Legal kind="cookies" />} />
                                <Route path="/promocao-24h" element={<Promocao24h />} />
                                <Route path="/unsubscribe" element={<Unsubscribe />} />
                                <Route path="/descadastrar" element={<Unsubscribe />} />
                                <Route path="/newsletter/confirmar" element={<NewsletterConfirm />} />
                                <Route path="/checkout" element={<Checkout />} />
                                <Route path="/retomar/:token" element={<RetomarCarrinho />} />
                                <Route path="/reset-password" element={<ResetPassword />} />
                                <Route path="/grupos" element={<Grupos />} />
                                <Route path="/admin/grupos-whatsapp" element={<Navigate to="/admin/configuracoes" replace />} />
                                <Route path="/pagar-complemento/:orderNumber" element={<PagarComplemento />} />
                                <Route path="/pagar/:orderId" element={<PagarLink />} />
                                <Route path="/outlet-vip" element={<OutletVip />} />
                                <Route path="/catalogo" element={<Catalogo />} />
                                <Route path="/catalogo/live" element={<Catalogo collection={LIVE_COLLECTION} />} />
                                <Route path="/catalogo/live/:catalogoSlug" element={<Catalogo collection={LIVE_COLLECTION} />} />
                                <Route path="/catalogo/:catalogoSlug" element={<Catalogo />} />
                                {isCatalogoHost && (
                                  <Route path="/live" element={<Catalogo collection={LIVE_COLLECTION} />} />
                                )}
                                {isCatalogoHost && (
                                  <Route path="/live/:catalogoSlug" element={<Catalogo collection={LIVE_COLLECTION} />} />
                                )}
                                {isCatalogoHost && (
                                  <Route path="/:catalogoSlug" element={<Catalogo />} />
                                )}

                                <Route path="/admin/login" element={<AdminLogin />} />
                                <Route path="/admin" element={<AdminDashboard />} />
                                <Route path="/admin/pdv" element={<AdminRailShell><AdminPDV /></AdminRailShell>} />
                                <Route path="/admin/pedido-manual" element={<AdminManualOrder />} />
                                <Route path="/admin/calculadora-frete" element={<AdminCalculadoraFrete />} />
                                <Route path="/admin/separacao" element={<AdminFulfillment />} />
                                <Route path="/admin/produtos" element={<AdminProducts />} />
                                <Route path="/admin/fotos" element={<AdminStudioPhotos />} />
                                <Route path="/admin/fotos/enviar" element={<AdminStudioUpload />} />
                                <Route path="/admin/fotos/sessao/:seedPhotoId" element={<AdminStudioPhotoSession />} />
                                <Route path="/admin/pedidos" element={<AdminOrders />} />
                                <Route path="/admin/clientes" element={<AdminCustomers />} />
                                <Route path="/admin/gestao-devolucoes" element={<AdminExchanges />} />
                                <Route path="/admin/devolucoes" element={<Navigate to="/admin/gestao-devolucoes" replace />} />
                                <Route path="/admin/trocas" element={<Navigate to="/admin/gestao-devolucoes" replace />} />

                                <Route path="/admin/configuracoes" element={<AdminSettings />} />
                                <Route path="/admin/diagnostico" element={<AdminDiagnostico />} />
                                <Route path="/admin/tiktok-shop" element={<AdminTikTokShop />} />
                                <Route path="/admin/recuperar" element={<AdminRecovery />} />
                                <Route path="/admin/origem-vendas" element={<AdminAtribuicao />} />
                                <Route path="/admin/avaliacoes" element={<AdminReviews />} />
                                <Route path="/admin/conversao" element={<AdminConversao />} />
                                <Route path="/admin/desempenho-produtos" element={<AdminDesempenhoProdutos />} />
                                <Route path="/admin/automacoes" element={<AdminMarketing />} />
                                <Route path="/admin/campanhas" element={<AdminCampanhas />} />
                                <Route path="/admin/beneficios" element={<AdminBeneficios />} />
                                <Route path="/admin/cupons" element={<AdminCoupons />} />
                                <Route path="/admin/regras-quantidade" element={<AdminQuantityRules />} />
                                <Route path="/admin/descontos-campanha" element={<AdminCampaignDiscounts />} />
                               <Route path="/admin/tarefas" element={<AdminTarefas />} />
                               <Route path="/admin/tarefas/monitor" element={<AdminTaskMonitor />} />
                               <Route path="/admin/notificacoes" element={<AdminNotifications />} />
                               <Route path="/admin/notificacoes/configuracoes" element={<AdminNotificationsSettings />} />
                               <Route path="/admin/notificacoes/painel" element={<AdminNotificationsPanel />} />
                                <Route path="/admin/layout-site" element={<AdminLayoutSite />} />
                                <Route path="/admin/novidades" element={<AdminNovidades />} />
                                <Route path="/admin/lancamentos" element={<AdminLancamentos />} />
                                <Route path="/admin/central-lancamentos" element={<LaunchCentralRedirect />} />
                                <Route path="/admin/central-lancamentos/revisao/arquivados" element={<AdminCentralLancamentos dept="revisao" archived />} />
                                <Route path="/admin/central-lancamentos/revisao" element={<AdminCentralLancamentos dept="revisao" />} />
                                <Route path="/admin/central-lancamentos/fotografia" element={<AdminCentralLancamentos dept="fotografia" />} />
                                <Route path="/admin/central-lancamentos/marketing" element={<AdminCentralLancamentos dept="marketing" />} />
                                <Route path="/admin/conferir-pix" element={<AdminConferirPix />} />
                                <Route path="/admin/gastos-pendentes" element={<AdminGastosPendentes />} />
                                <Route path="/admin/dre-gerencial" element={<Navigate to="/admin/dre" replace />} />
                                <Route path="/admin/compre-o-look" element={<AdminCompreLook />} />

                                <Route path="/admin/analise-produtos" element={<AdminProductAnalysis />} />
                                <Route path="/admin/suprimentos" element={<AdminSupplies />} />
                                <Route path="/admin/brindes" element={<AdminOutrosProdutos />} />
                                <Route path="/admin/colecoes-inteligentes" element={<AdminSmartCollections />} />
                                <Route path="/admin/ordem-vitrine" element={<AdminVitrineOrdem />} />
                                <Route path="/admin/banner-home" element={<AdminBannerHome />} />
                                <Route path="/admin/banners-vitrine" element={<AdminBannerVitrine />} />
                                <Route path="/admin/catalogo" element={<AdminCatalogoOutlet />} />
                                <Route path="/admin/notas-fiscais" element={<AdminFiscal />} />
                                <Route path="/admin/etiquetas" element={<AdminEtiquetas />} />
                                <Route path="/admin/historico" element={<AdminEstoque />} />
                                <Route path="/admin/transferencias" element={<AdminTransferencias />} />
                                <Route path="/admin/reservas" element={<AdminReservas />} />
                                <Route path="/admin/transferencias/nova" element={<AdminEstoqueRegistrar />} />
                                <Route path="/admin/costureira" element={<AdminServicos serviceType="costureira" />} />
                                <Route path="/admin/costureira/nova" element={<AdminServicoNovo serviceType="costureira" />} />
                                <Route path="/admin/lavagem" element={<AdminServicos serviceType="lavagem" />} />
                                <Route path="/admin/lavagem/nova" element={<AdminServicoNovo serviceType="lavagem" />} />
                                <Route path="/admin/servicos" element={<AdminServicos />} />
                                <Route path="/admin/servicos/nova" element={<AdminServicoNovo serviceType="costureira" />} />
                                <Route path="/admin/saidas" element={<PageEditGuard permKey="stock_exits"><AdminSaidas /></PageEditGuard>} />
                                <Route path="/admin/revisao" element={<AdminRevisao />} />
                                <Route path="/admin/saidas/nova" element={<RequireEditPermission permKey="stock_exits" redirectTo="/admin/saidas"><AdminSaidaNova /></RequireEditPermission>} />
                                <Route path="/admin/entradas" element={<PageEditGuard permKey="stock_entries"><AdminEntradas /></PageEditGuard>} />
                                <Route path="/admin/entradas/nova" element={<RequireEditPermission permKey="stock_entries" redirectTo="/admin/entradas"><AdminEntradaNova /></RequireEditPermission>} />

                                <Route path="/admin/inventario" element={<AdminInventoryList />} />
                                <Route path="/admin/inventario/:id" element={<AdminInventoryCount />} />
                                <Route path="/admin/curadoria-arara" element={<AdminRackCuration />} />
                                <Route path="/admin/email-template/:id" element={<AdminRailShell><AdminEmailTemplateEditor /></AdminRailShell>} />
                                <Route path="/admin/tela-pdv" element={<PDVClienteDisplay />} />
                                <Route path="/admin/tela-vitrine" element={<AdminTelaVitrine />} />
                                <Route path="/tela-promo" element={<TelaPromo />} />
                                <Route path="/admin/pdv/cliente" element={<PDVClienteDisplay />} />
                                <Route path="/admin/autoatendimento" element={<PDVAutoatendimento />} />
                                <Route path="/admin/usuarios" element={<AdminEmployees />} />
                                <Route path="/admin/rodizio" element={<AdminRailShell><AdminRodizio /></AdminRailShell>} />
                                <Route path="/admin/rodizio/tv" element={<AdminRodizioTV />} />
                                <Route path="/admin/separacao-tv" element={<AdminSeparacaoTV />} />
                                <Route path="/admin/expediente" element={<AdminWorkSchedule />} />
                                <Route path="/admin/ponto" element={<AdminTimeClock />} />
                                <Route path="/admin/registros-ponto" element={<AdminTimeRecords />} />
                                <Route path="/admin/calendario-loja" element={<AdminStoreCalendar />} />
                                <Route path="/admin/metas" element={<AdminSalesGoals />} />
                                <Route path="/admin/minhas-metas" element={<AdminMinhasMetas />} />
                                <Route path="/admin/meu-calendario" element={<AdminRailShell><AdminMeuCalendario /></AdminRailShell>} />
                                <Route path="/admin/caixas" element={<AdminCaixas />} />
                                <Route path="/admin/rfid" element={<AdminRFID />} />
                                <Route path="/admin/feedbacks" element={<AdminFeedback />} />
                                <Route path="/admin/status" element={<AdminSystemStatus />} />
                                <Route path="/admin/imprimir-etiqueta" element={<AdminShippingLabelPrint />} />
                                <Route path="/admin/gastos" element={<AdminExpenses />} />
                                <Route path="/admin/dre" element={<AdminDRE />} />
                                <Route path="/admin/compras" element={<AdminCompras />} />
                                <Route path="/admin/familia" element={<AdminFamilyExpenses />} />
                                <Route path="/admin/vagas" element={<AdminVagas />} />
                                <Route path="/admin/movimentacoes" element={<AdminMovimentacoes />} />
                                <Route path="/admin/servicos-externos" element={<AdminServicosExternos />} />
                                <Route path="/admin/colaboradores" element={<AdminColaboradoresHub />} />
                                <Route path="/admin/ouvidoria" element={<AdminOuvidoria />} />
                                <Route path="/ouvidoria" element={<Ouvidoria />} />

                                <Route path="*" element={<NotFound />} />
                              </Routes>
                            </Suspense>
                            </RouteErrorBoundary>
                          </PublicRouteWrapper>
                      </AdminThemeProvider>
                    </BrowserRouter>
                  </TooltipProvider>
                </CartProvider>
              </FavoritesProvider>
          </SizePreferenceProvider>
        </SiteThemeProvider>
      </QueryClientProvider>
    </HelmetProvider>
  </ErrorBoundary>
);

export default App;
